(function () {
  'use strict';

  if (customElements.get('product-banner-marquee')) return;

  var DEFAULT_SPEED = 60; // pixels per second

  /**
   * <product-banner-marquee data-speed>
   *
   * The scrolling itself is pure CSS (the track is duplicated in Liquid so the
   * loop is seamless). This element only measures things the stylesheet cannot:
   *
   *  - how far one track is, so the animation travels exactly one track width
   *    no matter how many messages the merchant entered;
   *  - how many duplicate tracks are needed to cover the viewport, so short
   *    message lists never leave a gap mid-loop;
   *  - the duration, derived from a pixels-per-second speed setting.
   *
   * Under prefers-reduced-motion it removes its own measurements and the
   * stylesheet falls back to a static, wrapped row.
   */
  class ProductBannerMarquee extends HTMLElement {
    constructor() {
      super();
      this.measure = this.measure.bind(this);
      this.schedule = this.schedule.bind(this);
      this.frame = null;
      this.resizeObserver = null;
      this.motionQuery = null;
    }

    connectedCallback() {
      this.marquee = this.querySelector('.product-banner__marquee');
      this.track = this.querySelector('.product-banner__track');
      this.clone = this.querySelector('[data-banner-clone]');
      if (!this.marquee || !this.track || !this.clone) return;

      var speed = parseFloat(this.dataset.speed);
      this.speed = speed > 0 ? speed : DEFAULT_SPEED;

      if (window.matchMedia) {
        this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        if (this.motionQuery.addEventListener) {
          this.motionQuery.addEventListener('change', this.schedule);
        } else if (this.motionQuery.addListener) {
          this.motionQuery.addListener(this.schedule);
        }
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.schedule);
        this.resizeObserver.observe(this);
      } else {
        window.addEventListener('resize', this.schedule);
      }

      this.schedule();
    }

    disconnectedCallback() {
      if (this.frame !== null) {
        window.cancelAnimationFrame(this.frame);
        this.frame = null;
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      window.removeEventListener('resize', this.schedule);
      if (this.motionQuery) {
        if (this.motionQuery.removeEventListener) {
          this.motionQuery.removeEventListener('change', this.schedule);
        } else if (this.motionQuery.removeListener) {
          this.motionQuery.removeListener(this.schedule);
        }
        this.motionQuery = null;
      }
    }

    schedule() {
      // requestAnimationFrame never fires while the document is hidden, so a
      // page opened in a background tab would stay unmeasured. Measure inline
      // in that case — layout is still available.
      if (document.visibilityState === 'hidden') {
        this.measure();
        return;
      }
      if (this.frame !== null) window.cancelAnimationFrame(this.frame);
      this.frame = window.requestAnimationFrame(this.measure);
    }

    measure() {
      this.frame = null;
      if (!this.isConnected || !this.track) return;

      if (this.motionQuery && this.motionQuery.matches) {
        this.removeAttribute('data-measured');
        this.style.removeProperty('--product-banner-distance');
        this.style.removeProperty('--product-banner-duration');
        return;
      }

      var width = this.track.getBoundingClientRect().width;
      if (!width) return;

      var viewport = this.getBoundingClientRect().width || width;
      this.ensureClones(Math.max(1, Math.ceil(viewport / width)));

      this.style.setProperty('--product-banner-distance', Math.round(width) + 'px');
      this.style.setProperty('--product-banner-duration', (width / this.speed).toFixed(2) + 's');
      this.setAttribute('data-measured', '');
    }

    // Keeps `count` duplicate tracks after the readable one. Never removes the
    // duplicate rendered by Liquid, so the CSS-only fallback stays intact.
    ensureClones(count) {
      var clones = this.marquee.querySelectorAll('[data-banner-clone]');
      if (clones.length >= count) return;

      var fragment = document.createDocumentFragment();
      for (var i = clones.length; i < count; i++) {
        fragment.appendChild(this.clone.cloneNode(true));
      }
      this.marquee.appendChild(fragment);
    }
  }

  customElements.define('product-banner-marquee', ProductBannerMarquee);
})();
